import React from 'react';

export type AppMode = 'single' | 'multi';

interface ModeSelectorProps {
  onSelect: (mode: AppMode) => void;
}

export const ModeSelector: React.FC<ModeSelectorProps> = ({ onSelect }) => {
  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: 'linear-gradient(135deg, #0a0f1e 0%, #0d1f3c 50%, #0a1628 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Inter', 'Roboto', sans-serif",
      color: '#fff',
      overflow: 'hidden',
    }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 56 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 12, marginBottom: 16,
        }}>
          {/* Logo icon */}
          <div style={{
            width: 44, height: 44, borderRadius: 10,
            background: 'linear-gradient(135deg, #cc0000, #ff3333)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 24px rgba(204,0,0,0.5)',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="5" width="20" height="14" rx="2" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="1.5"/>
              <circle cx="12" cy="12" r="3.5" fill="white"/>
              <line x1="7" y1="9" x2="7" y2="9" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 4, color: '#cc0000', fontWeight: 700, textTransform: 'uppercase' }}>
              Buzz24
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.5, color: '#fff', lineHeight: 1 }}>
              News Video Studio
            </div>
          </div>
        </div>
        <p style={{ fontSize: 14, color: '#64748b', margin: 0, letterSpacing: 0.3 }}>
          Professional Television News Video Creator
        </p>
      </div>

      {/* Mode Cards */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'stretch' }}>

        {/* Single News Card */}
        <ModeCard
          icon={
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <rect x="4" y="8" width="28" height="20" rx="3" fill="#1d4ed8" fillOpacity="0.3" stroke="#3b82f6" strokeWidth="1.5"/>
              <rect x="8" y="13" width="10" height="8" rx="1.5" fill="#3b82f6" fillOpacity="0.5"/>
              <rect x="21" y="13" width="9" height="2.5" rx="1" fill="#93c5fd"/>
              <rect x="21" y="17" width="6" height="2" rx="1" fill="#93c5fd" fillOpacity="0.6"/>
              <rect x="4" y="26" width="28" height="3" rx="1" fill="#1e3a8a"/>
            </svg>
          }
          title="Single News"
          subtitle="एक खबर · One Story"
          description="Create a single professional news video with custom template, media assets, lower third, ticker, and animations."
          features={['Single story', 'Custom template', 'Manual layer control', 'Quick render']}
          color="#3b82f6"
          badge="Current Mode"
          onClick={() => onSelect('single')}
        />

        {/* Multi Story Card */}
        <ModeCard
          icon={
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <rect x="4" y="4" width="28" height="6" rx="2" fill="#dc2626" fillOpacity="0.3" stroke="#ef4444" strokeWidth="1.5"/>
              <rect x="4" y="13" width="13" height="9" rx="1.5" fill="#7c3aed" fillOpacity="0.3" stroke="#8b5cf6" strokeWidth="1.2"/>
              <rect x="19" y="13" width="13" height="9" rx="1.5" fill="#7c3aed" fillOpacity="0.3" stroke="#8b5cf6" strokeWidth="1.2"/>
              <rect x="4" y="25" width="13" height="7" rx="1.5" fill="#7c3aed" fillOpacity="0.2" stroke="#8b5cf6" strokeWidth="1.2"/>
              <rect x="19" y="25" width="13" height="7" rx="1.5" fill="#7c3aed" fillOpacity="0.2" stroke="#8b5cf6" strokeWidth="1.2"/>
              <circle cx="18" cy="7" r="1.5" fill="#ef4444"/>
            </svg>
          }
          title="Multi Story Show"
          subtitle="Top 10 · Bulletin · Breaking Show"
          description="Automatically compose a complete television-style news bulletin with multiple stories, transitions, story counter, and master timeline."
          features={['Top 10 / Top 20 / Bulletin', 'Auto scene generation', 'Story counter (खबर नंबर 1)', 'Master timeline']}
          color="#8b5cf6"
          badge="New"
          isNew
          onClick={() => onSelect('multi')}
        />
      </div>

      {/* Footer */}
      <div style={{ marginTop: 48, fontSize: 11, color: '#1e3a5f', letterSpacing: 1 }}>
        BUZZ24 VIDEO STUDIO · PROFESSIONAL EDITION
      </div>
    </div>
  );
};

// ─── Mode Card Component ──────────────────────────────────────

interface ModeCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  color: string;
  badge: string;
  isNew?: boolean;
  onClick: () => void;
}

const ModeCard: React.FC<ModeCardProps> = ({
  icon, title, subtitle, description, features, color, badge, isNew, onClick,
}) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 300,
        background: hovered
          ? `linear-gradient(135deg, rgba(${hexToRgb(color)},0.15) 0%, #0f172a 100%)`
          : 'rgba(15, 23, 42, 0.8)',
        border: `1.5px solid ${hovered ? color : 'rgba(255,255,255,0.08)'}`,
        borderRadius: 16,
        padding: '28px 24px',
        cursor: 'pointer',
        textAlign: 'left',
        color: '#fff',
        transition: 'all 0.2s ease',
        boxShadow: hovered
          ? `0 0 32px rgba(${hexToRgb(color)}, 0.25), 0 8px 32px rgba(0,0,0,0.4)`
          : '0 4px 24px rgba(0,0,0,0.3)',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Glow top border */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: hovered ? `linear-gradient(90deg, transparent, ${color}, transparent)` : 'transparent',
        transition: 'all 0.2s',
      }} />

      {/* Badge */}
      <div style={{
        position: 'absolute', top: 14, right: 14,
        background: isNew
          ? 'linear-gradient(135deg, #7c3aed, #c026d3)'
          : 'rgba(59,130,246,0.2)',
        color: isNew ? '#fff' : '#93c5fd',
        fontSize: 9, fontWeight: 700, letterSpacing: 1,
        textTransform: 'uppercase',
        padding: '3px 8px', borderRadius: 20,
        border: isNew ? 'none' : '1px solid rgba(59,130,246,0.3)',
      }}>
        {badge}
      </div>

      {/* Icon */}
      <div style={{ marginBottom: 16 }}>{icon}</div>

      {/* Title */}
      <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4, letterSpacing: -0.3 }}>
        {title}
      </div>

      {/* Subtitle */}
      <div style={{ fontSize: 11, color, fontWeight: 600, letterSpacing: 0.5, marginBottom: 14, textTransform: 'uppercase' }}>
        {subtitle}
      </div>

      {/* Description */}
      <div style={{ fontSize: 12.5, color: '#94a3b8', lineHeight: 1.6, marginBottom: 20 }}>
        {description}
      </div>

      {/* Features */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
        {features.map((f) => (
          <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: '#cbd5e1' }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="6" r="5.5" fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1"/>
              <path d="M3.5 6l1.8 1.8L8.5 4" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {f}
          </div>
        ))}
      </div>

      {/* CTA */}
      <div style={{
        background: hovered ? color : 'transparent',
        border: `1.5px solid ${color}`,
        borderRadius: 8,
        padding: '10px 16px',
        fontSize: 13,
        fontWeight: 700,
        color: hovered ? '#fff' : color,
        textAlign: 'center',
        transition: 'all 0.2s',
        letterSpacing: 0.3,
      }}>
        {hovered ? '→ Open Studio' : 'Select Mode'}
      </div>
    </button>
  );
};

// ─── Hex to RGB helper ────────────────────────────────────────
function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}
